(() => {
  const state = {
    products: null,
    loading: null,
  };

  const normalize = (value) => String(value || '')
    .normalize('NFKC')
    .replace(/[\u3000\s]+/g, ' ')
    .replace(/[‐‑‒–—−]/g, '-')
    .replace(/[“”"']/g, '')
    .trim()
    .toLowerCase();

  const queryTokens = (value) => normalize(value)
    .split(/[|｜/／・,，、。()（）［］\[\]「」『』]+|\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2);

  const routeRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const productUrl = (handle) => `${routeRoot()}products/${handle}`;

  const productsEndpoint = (page) => `/collections/all/products.json?limit=250&page=${page}`;

  const fetchPublishedProducts = async () => {
    if (state.products) return state.products;
    if (state.loading) return state.loading;

    state.loading = (async () => {
      const products = [];
      const maxPages = 20;

      for (let page = 1; page <= maxPages; page += 1) {
        const response = await fetch(productsEndpoint(page), { credentials: 'same-origin' });
        if (!response.ok) break;

        const data = await response.json();
        const batch = Array.isArray(data.products) ? data.products : [];
        products.push(...batch);

        if (batch.length < 250) break;
      }

      state.products = products;
      return products;
    })();

    return state.loading;
  };

  const searchableText = (product) => normalize([
    product.title,
    product.vendor,
    product.product_type,
    product.handle,
    Array.isArray(product.tags) ? product.tags.join(' ') : '',
  ].join(' '));

  const matchesQuery = (product, tokens) => {
    const text = searchableText(product);
    return tokens.every((token) => text.includes(token));
  };

  const money = (price) => {
    const amount = Number(price || 0);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ja-JP', {
        style: 'currency',
        currency: 'JPY',
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return `¥${amount.toLocaleString('ja-JP')}`;
    }
  };

  const productPrice = (product) => {
    const variant = Array.isArray(product.variants) ? product.variants[0] : null;
    return variant ? money(variant.price) : '';
  };

  const productAvailable = (product) => {
    return Array.isArray(product.variants) && product.variants.some((variant) => variant.available);
  };

  const productImage = (product) => {
    if (Array.isArray(product.images) && product.images[0] && product.images[0].src) {
      return product.images[0].src;
    }
    return '';
  };

  const displayVendor = (vendor) => {
    const value = String(vendor || '').trim();
    if (!value) return '';

    if (value.includes('｜')) {
      return value.split('｜').pop().trim();
    }

    if (value.includes('|')) {
      return value.split('|').pop().trim();
    }

    return value;
  };

  const soldOutLabel = () => {
    const lang = document.documentElement.lang || '';
    if (lang.startsWith('zh')) return 'Sold out';
    if (lang.startsWith('en')) return 'Sold out';
    return '売り切れ';
  };

  const emptyLabel = () => {
    const lang = document.documentElement.lang || '';
    if (lang.startsWith('zh')) return '未找到匹配商品。';
    if (lang.startsWith('en')) return 'No matching products found.';
    return '一致する商品は見つかりませんでした。';
  };

  const resultCountLabel = (count) => {
    const lang = document.documentElement.lang || '';
    if (lang.startsWith('zh')) return `${count} 件商品`;
    if (lang.startsWith('en')) return `${count} results`;
    return `${count}件の結果`;
  };

  const resultTitleLabel = (query, count) => {
    const lang = document.documentElement.lang || '';
    const suffix = document.title.includes(' – ') ? ` – ${document.title.split(' – ').pop()}` : '';

    if (lang.startsWith('zh')) return `搜索：“${query}”的结果 ${count} 件${suffix}`;
    if (lang.startsWith('en')) return `Search: ${count} results for “${query}”${suffix}`;
    return `検索:「${query}」の検索結果${count}件${suffix}`;
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const renderProduct = (product) => {
    const image = productImage(product);
    const imageMarkup = image
      ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
      : '<div class="bunbuku-admin-search-card__placeholder"></div>';
    const badge = productAvailable(product) ? '' : `<span class="bunbuku-admin-search-card__badge">${soldOutLabel()}</span>`;

    return `
      <li class="grid__item bunbuku-admin-search-card">
        <a class="bunbuku-admin-search-card__link" href="${escapeHtml(productUrl(product.handle))}">
          <span class="bunbuku-admin-search-card__media">
            ${imageMarkup}
            ${badge}
          </span>
          <span class="bunbuku-admin-search-card__title">${escapeHtml(product.title)}</span>
          <span class="bunbuku-admin-search-card__vendor">${escapeHtml(displayVendor(product.vendor))}</span>
          <span class="bunbuku-admin-search-card__price">${escapeHtml(productPrice(product))}</span>
        </a>
      </li>
    `;
  };

  const ensureStyles = () => {
    if (document.getElementById('bunbuku-admin-search-styles')) return;

    const style = document.createElement('style');
    style.id = 'bunbuku-admin-search-styles';
    style.textContent = `
      .bunbuku-admin-search-card__link {
        color: inherit;
        display: block;
        text-align: center;
        text-decoration: none;
      }

      .bunbuku-admin-search-card__media {
        align-items: center;
        background: rgba(var(--color-foreground), 0.015);
        display: flex;
        justify-content: center;
        min-height: 220px;
        position: relative;
      }

      .bunbuku-admin-search-card__media img {
        display: block;
        height: auto;
        max-height: 330px;
        max-width: 100%;
        object-fit: contain;
        width: auto;
      }

      .bunbuku-admin-search-card__placeholder {
        aspect-ratio: 1 / 1;
        width: 100%;
      }

      .bunbuku-admin-search-card__badge {
        background: rgb(var(--color-foreground));
        border-radius: 999px;
        bottom: 1rem;
        color: rgb(var(--color-background));
        font-size: 1.2rem;
        left: 1rem;
        line-height: 1;
        padding: 0.6rem 1.1rem;
        position: absolute;
        transform: none;
        white-space: nowrap;
      }

      .bunbuku-admin-search-card__title,
      .bunbuku-admin-search-card__vendor,
      .bunbuku-admin-search-card__price {
        display: block;
      }

      .bunbuku-admin-search-card__title {
        font-size: 1.5rem;
        line-height: 1.45;
        margin-top: 1.8rem;
      }

      .bunbuku-admin-search-card__vendor {
        color: rgba(var(--color-foreground), 0.68);
        font-size: 1.25rem;
        letter-spacing: 0.08em;
        line-height: 1.45;
        margin-top: 0.45rem;
        text-transform: uppercase;
      }

      .bunbuku-admin-search-card__price {
        font-size: 1.8rem;
        line-height: 1.2;
        margin-top: 1rem;
      }

      .bunbuku-admin-search-empty {
        grid-column: 1 / -1;
        list-style: none;
        padding: 2rem 0;
      }
    `;
    document.head.appendChild(style);
  };

  const updateCounts = (count) => {
    const text = resultCountLabel(count);
    document.querySelectorAll('#ProductCountDesktop, #ProductCount, .product-count__text span, [role="status"]').forEach((element) => {
      if (/\d+/.test(element.textContent)) element.textContent = text;
    });
  };

  const hidePagination = () => {
    document.querySelectorAll('.pagination-wrapper').forEach((wrapper) => {
      wrapper.hidden = true;
      wrapper.style.display = 'none';
    });
  };

  const renderResults = (products, query) => {
    const grid = document.querySelector('#product-grid .product-grid');
    if (!grid) return;

    grid.innerHTML = products.length
      ? products.map(renderProduct).join('')
      : `<li class="bunbuku-admin-search-empty">${emptyLabel()}</li>`;

    updateCounts(products.length);
    document.title = resultTitleLabel(query, products.length);
    hidePagination();
  };

  const run = async () => {
    if (!window.location.pathname.includes('/search')) return;

    document.querySelectorAll('input[name="options[prefix]"]').forEach((input) => input.remove());

    const query = new URLSearchParams(window.location.search).get('q') || '';
    const tokens = queryTokens(query);
    if (!tokens.length) return;

    ensureStyles();

    try {
      const products = await fetchPublishedProducts();
      renderResults(products.filter((product) => matchesQuery(product, tokens)), query);
    } catch (error) {
      // Keep Shopify's native results if the custom product index cannot load.
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', run);
})();